import { Directive, HostListener, ElementRef, inject } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Input restriction directive for date fields
 *
 * - Only allows digits and dots
 * - Limits input to 10 characters (DD.MM.YYYY)
 * - Validates format on blur
 * - Sets custom error state that persists even if field is cleared
 *
 * Usage:
 * ```html
 * <input matInput [matDatepicker]="picker" appDateInputRestriction />
 * ```
 */
@Directive({
  selector: 'input[matInput][matDatepicker][appDateInputRestriction]',
  standalone: true,
})
export class DateInputRestrictionDirective {
  private el = inject(ElementRef<HTMLInputElement>);
  private ngControl = inject(NgControl, { optional: true });
  private lastInputValue = '';

  constructor() {}

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = this.el.nativeElement;
    this.lastInputValue = input.value || '';
  }

  @HostListener('blur')
  onBlur(): void {
    if (!this.ngControl?.control) return;

    const input = this.el.nativeElement;
    const value = this.lastInputValue.trim();

    // If there's invalid input that was typed but got cleared by Angular Material
    if (value && !this.ngControl.control.value) {
      // Set a custom validation error
      const errors = this.ngControl.control.errors || {};
      this.ngControl.control.setErrors({
        ...errors,
        matDatepickerParse: { text: value },
      });
      this.ngControl.control.markAsTouched();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const input = this.el.nativeElement;
    const key = event.key;

    // Allow control keys
    if (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'Tab' ||
      key === 'Escape' ||
      key === 'Enter' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'Home' ||
      key === 'End' ||
      event.metaKey ||
      event.ctrlKey // Allow Cmd/Ctrl shortcuts
    ) {
      return;
    }

    // Only allow digits and dots
    if (!/^[0-9.]$/.test(key)) {
      event.preventDefault();
      return;
    }

    // Block input if max length reached (10 chars: DD.MM.YYYY)
    const value = input.value || '';
    const hasSelection =
      input.selectionStart !== null &&
      input.selectionEnd !== null &&
      input.selectionStart !== input.selectionEnd;

    if (value.length >= 10 && !hasSelection) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const input = this.el.nativeElement;
    const pastedText = event.clipboardData?.getData('text') || '';

    // Only allow if pasted text contains only digits and dots
    if (!/^[0-9.]*$/.test(pastedText)) {
      event.preventDefault();
      return;
    }

    // Check if result would exceed max length
    const currentValue = input.value || '';
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;
    const beforeSelection = currentValue.substring(0, selectionStart);
    const afterSelection = currentValue.substring(selectionEnd);
    const resultValue = beforeSelection + pastedText + afterSelection;

    if (resultValue.length > 10) {
      event.preventDefault();
    }
  }
}
